import { useSessionLockStore } from "@/stores/sessionLockStore";
import { useAuthStore } from "@/stores/authStore";
import { useAccessStore } from "@/stores/accessStore";

/** Set VUE_APP_IDLE_LOCK_ENABLED=false to disable idle session lock. */
export function isIdleLockEnabled() {
  const raw = String(process.env.VUE_APP_IDLE_LOCK_ENABLED ?? "true").toLowerCase();
  return raw !== "false" && raw !== "0" && raw !== "off";
}

const IDLE_MS =
  (Number(process.env.VUE_APP_IDLE_LOCK_MINUTES) || 15) * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

let idleTimer = null;
let lastThrottleAt = 0;
let listenersBound = false;
let active = false;

function getStores() {
  return {
    auth: useAuthStore(),
    lock: useSessionLockStore(),
  };
}

function canTrackIdle() {
  if (!isIdleLockEnabled()) return false;
  const { auth, lock } = getStores();
  return auth.isAuthenticated && !lock.isLocked;
}

function clearIdleTimer() {
  if (idleTimer) {
    window.clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function triggerLock(returnPath) {
  const { lock } = getStores();
  if (lock.isLocked) return;
  lock.lock(returnPath || window.location.pathname + window.location.search);
}

function scheduleIdleTimer(returnPath = "/") {
  clearIdleTimer();
  if (!canTrackIdle()) return;

  idleTimer = window.setTimeout(() => {
    triggerLock(returnPath);
  }, IDLE_MS);
}

function onActivity() {
  if (!canTrackIdle()) return;

  const now = Date.now();
  if (now - lastThrottleAt < ACTIVITY_THROTTLE_MS) return;
  lastThrottleAt = now;

  const { lock } = getStores();
  lock.touchActivity();
  scheduleIdleTimer(window.location.pathname + window.location.search);
}

function refreshAccessIfStale() {
  const { auth, lock } = getStores();
  if (!auth.isAuthenticated || lock.isLocked) return;

  const access = useAccessStore();
  if (!access.loaded || access.status === "none") {
    access.fetchAccess({ force: true }).catch(() => {});
  }
}

function onVisibilityChange() {
  const { auth, lock } = getStores();
  if (document.visibilityState !== "visible") return;
  if (!auth.isAuthenticated || lock.isLocked) return;

  const elapsed = Date.now() - (lock.lastActivityAt || Date.now());
  if (elapsed >= IDLE_MS) {
    triggerLock(lock.returnPath || window.location.pathname + window.location.search);
    return;
  }

  scheduleIdleTimer(window.location.pathname + window.location.search);
  refreshAccessIfStale();
}

function bindListeners() {
  if (listenersBound) return;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onActivity, { passive: true });
  });
  document.addEventListener("visibilitychange", onVisibilityChange);
  listenersBound = true;
}

function unbindListeners() {
  if (!listenersBound) return;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, onActivity);
  });
  document.removeEventListener("visibilitychange", onVisibilityChange);
  listenersBound = false;
}

export function startIdleLock() {
  if (!isIdleLockEnabled()) {
    stopIdleLock();
    getStores().lock.reset();
    return;
  }

  if (active) {
    scheduleIdleTimer(window.location.pathname + window.location.search);
    return;
  }

  active = true;
  bindListeners();

  const { lock } = getStores();
  lock.touchActivity();
  scheduleIdleTimer(window.location.pathname + window.location.search);
}

export function stopIdleLock() {
  active = false;
  clearIdleTimer();
  unbindListeners();
}

export function restartIdleLock() {
  stopIdleLock();
  if (!isIdleLockEnabled()) {
    getStores().lock.reset();
    return;
  }
  if (useAuthStore().isAuthenticated) {
    startIdleLock();
  }
}

export function getIdleLockTimeoutMs() {
  return IDLE_MS;
}
